import type { AppType } from '@hudhuria/api';
import { hc } from 'hono/client';
import { storage } from './storage';

/**
 * API base: same origin in the browser (Vite proxies /api in dev); the absolute VITE_API_URL inside
 * the Capacitor app, where the page is served from capacitor://localhost (the native build mode
 * refuses to build without it).
 */
export const API_BASE: string = import.meta.env.VITE_API_URL || window.location.origin;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public field?: string,
  ) {
    super(message);
  }
}

/** A fetch failure (no response) — treated as "offline", so writes stay queued. */
export const isNetworkError = (e: unknown) => !(e instanceof ApiError) || e.status === 0 || e.status >= 500;

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

export const getToken = () => storage.get('token');

async function apiFetch(input: RequestInfo | URL, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);
  let res: Response;
  try {
    res = await fetch(input, { ...init, headers });
  } catch {
    throw new ApiError(0, "Can't reach Hudhuria — check your connection");
  }
  return res;
}

export const api = hc<AppType>(API_BASE, { fetch: apiFetch }).api.v1;

/** Unwrap an hc response: JSON on success, ApiError otherwise. */
export async function ok<T>(p: Promise<Response> | Response): Promise<T> {
  const res = await p;
  if (res.status === 204) return undefined as T;
  if (res.ok) return (await res.json()) as T;
  let message = 'Something went wrong';
  let field: string | undefined;
  try {
    const body = (await res.json()) as { error?: string; field?: string };
    message = body.error ?? message;
    field = body.field;
  } catch {
    /* non-JSON error */
  }
  // A 401 from a sign-in endpoint means a wrong password or PIN (say, during "Switch staff"),
  // not that the current session ended.
  const signingIn = /\/auth\/(login|pin|desk)$/.test(new URL(res.url || 'http://x/').pathname);
  if (res.status === 401 && getToken() && !signingIn) onUnauthorized?.();
  throw new ApiError(res.status, message, field);
}

/** Absolute URL for API-relative paths such as signed photo URLs. */
export const apiUrl = (path: string | null | undefined) =>
  path ? (path.startsWith('http') || path.startsWith('data:') ? path : API_BASE + path) : null;
